import { useId } from 'react'

/**
 * A real sliding toggle, matching the design mock's `.tgl` (36×20 track, 16px
 * knob, green when on). A bare `<input type="checkbox" role="switch">` renders as
 * the platform checkbox, which is what the queue modal was showing instead.
 *
 * Accessibility (the ADA gate is 0 issues, and `role="switch"` is explicitly
 * called out in the project conventions):
 *  - a real <button role="switch"> with aria-checked — not a styled div, so it is
 *    keyboard-operable for free (Space/Enter) rather than mouse-only (WCAG 2.1.1)
 *  - the visible label is wired via <label htmlFor>, and a caller with no visible
 *    label must pass `aria-label`
 *  - disabled state is the real `disabled` attribute, so it drops out of the tab
 *    order instead of merely looking dimmed
 *
 * Colour: the ON track is `--color-cb-green`, which is decorative here — the
 * state is conveyed by aria-checked and the knob position, never by colour alone.
 */
const ToggleSwitch = ({
  checked = false,
  onChange,
  label,
  disabled = false,
  id,
  'aria-label': ariaLabel,
  labelClassName = '',
  children,
}) => {
  const autoId = useId()
  const switchId = id || `tgl-${autoId}`

  // An explicit aria-label wins; otherwise the <label htmlFor> names the switch.
  // Callers whose visible label carries extra chrome (a WIP badge) pass one so
  // the accessible name stays clean.
  const control = (
    <button
      type="button"
      id={switchId}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cb-accent/40 focus-visible:ring-offset-1 ${
        checked ? 'bg-cb-green' : 'bg-gray-300'
      } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      <span
        aria-hidden="true"
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  )

  if (!label && !children) return control

  return (
    <span className={`flex items-center gap-2 ${labelClassName}`}>
      {control}
      {/* htmlFor rather than wrapping: a <label> wrapping a <button> does not
          associate the two, and clicking the text would not toggle. */}
      <label htmlFor={switchId} className={disabled ? 'cursor-not-allowed' : 'cursor-pointer'}>
        {label}
        {children}
      </label>
    </span>
  )
}

export default ToggleSwitch
